import { useEffect, useRef } from "react";

let gmapsPromise = null;
function loadGoogleMaps(key) {
  if (window.google && window.google.maps && window.google.maps.places) return Promise.resolve(window.google.maps);
  if (gmapsPromise) return gmapsPromise;
  gmapsPromise = new Promise((resolve, reject) => {
    const cbName = "__gmapsReady";
    window[cbName] = () => resolve(window.google.maps);
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&loading=async&callback=${cbName}`;
    script.async = true;
    script.defer = true;
    script.onerror = reject;
    document.body.appendChild(script);
  });
  return gmapsPromise;
}

function ensurePacStyle() {
  if (document.getElementById("gmaps-pac-style")) return;
  const s = document.createElement("style");
  s.id = "gmaps-pac-style";
  s.textContent = ".pac-container{z-index:10000 !important;pointer-events:auto !important;}";
  document.head.appendChild(s);
}

export default function MapPicker({ lat, lon, onPick }) {
  const elRef = useRef(null);
  const inputRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    if (!apiKey) return;
    let cancelled = false;
    let observer;
    ensurePacStyle();
    loadGoogleMaps(apiKey).then((maps) => {
      if (cancelled || !elRef.current || mapRef.current) return;
      const hasStart = Number.isFinite(lat) && Number.isFinite(lon);
      const start = { lat: hasStart ? lat : 21.25, lng: hasStart ? lon : 81.63 };
      const map = new maps.Map(elRef.current, {
        center: start,
        zoom: hasStart ? 16 : 11,
        mapTypeId: "hybrid",
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        scrollwheel: false,
        gestureHandling: "cooperative",
      });
      const marker = new maps.Marker({ position: start, map, draggable: true });
      const place = (p, zoom) => {
        marker.setPosition(p);
        map.setCenter(p);
        if (zoom) map.setZoom(zoom);
        onPickRef.current(typeof p.lat === "function" ? p.lat() : p.lat, typeof p.lng === "function" ? p.lng() : p.lng);
      };
      marker.addListener("dragend", () => place(marker.getPosition()));
      map.addListener("click", (e) => place(e.latLng));

      if (inputRef.current && maps.places) {
        const ac = new maps.places.Autocomplete(inputRef.current, {
          fields: ["geometry", "name", "formatted_address"],
          componentRestrictions: { country: "in" },
        });
        ac.bindTo("bounds", map);
        ac.addListener("place_changed", () => {
          const sel = ac.getPlace();
          if (sel && sel.geometry && sel.geometry.location) place(sel.geometry.location, 17);
        });
        // Google appends .pac-container to document.body (outside the modal); stop its
        // pointer events from bubbling to Radix's outside-click detector so the dialog
        // doesn't close when a suggestion is clicked.
        const stop = (e) => e.stopPropagation();
        observer = new MutationObserver((muts) => {
          muts.forEach((mu) => mu.addedNodes.forEach((node) => {
            if (node.nodeType === 1 && node.classList && node.classList.contains("pac-container")) {
              node.addEventListener("pointerdown", stop);
              node.addEventListener("mousedown", stop);
            }
          }));
        });
        observer.observe(document.body, { childList: true });
      }
      mapRef.current = map;
      markerRef.current = marker;
    }).catch(() => {});
    return () => { cancelled = true; if (observer) observer.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mapRef.current && markerRef.current && Number.isFinite(lat) && Number.isFinite(lon)) {
      const cur = markerRef.current.getPosition();
      if (!cur || Math.abs(cur.lat() - lat) > 1e-6 || Math.abs(cur.lng() - lon) > 1e-6) {
        const pos = { lat, lng: lon };
        markerRef.current.setPosition(pos);
        mapRef.current.setCenter(pos);
      }
    }
  }, [lat, lon]);

  if (!apiKey) {
    return (
      <div data-testid="map-picker" className="flex items-center justify-center text-xs text-muted-foreground border rounded-lg bg-muted/30" style={{ height: 240, width: "100%" }}>
        Map unavailable — Google Maps API key not configured.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="text"
        placeholder="Search an address or place to drop the pin…"
        onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
        data-testid="map-search-input"
        className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      />
      <div ref={elRef} data-testid="map-picker" style={{ height: 240, width: "100%", borderRadius: 8, overflow: "hidden", zIndex: 0 }} />
    </div>
  );
}
